import { lazy, type ComponentType } from 'react';

import type { NavHandle } from '@/shared/navigation/nav-tree';

import { ErrorBoundary } from './error-boundary';

import type { NonIndexRouteObject, RouteObject } from 'react-router-dom';

type LazyImport = () => Promise<{ default: ComponentType }>;

/**
 * Lazy route with the shared ErrorBoundary always attached — a route built
 * here cannot forget its boundary. Named-export feature surfaces pass the
 * `.then(m => ({ default: m.X }))` wrapper as usual; `extra` merges the
 * remaining RouteObject fields (children, …). Index routes have no path and
 * stay outside the factory. `handle` is the nav-metadata contract (NavHandle):
 * label/icon/gate feed the sidebar tree, breadcrumb, and index redirects.
 */
export function lazyRoute(
  path: string,
  importFn: LazyImport,
  extra: Omit<NonIndexRouteObject, 'path' | 'element' | 'errorElement' | 'handle'> & {
    handle?: NavHandle;
  } = {}
): RouteObject {
  const Element = lazy(importFn);
  return { path, element: <Element />, errorElement: <ErrorBoundary />, ...extra };
}
